import { Button, Dialog, DialogActions, DialogContent, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { MprcDialogTitleClose, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ButtonCancel } from 'components/buttons';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { ChannelRoles, ChannelRolesSelectorResponse, GroupType, MemberType } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddSubordinates } from 'views/knowledge/channels/Home/EditChannel/EditChannelAddMembers/AddSubordinates';
import { AddTraining } from 'views/knowledge/channels/Home/EditChannel/EditChannelAddMembers/AddTraining';
import { AddValueOption } from 'views/knowledge/channels/Home/EditChannel/EditChannelListMembers';
import { useChannelMemberOperations } from 'views/knowledge/channels/Home/EditChannel/useChannelMemberOperations';
import { AddChannel } from './AddChannel';
import { AddGroups } from './AddGroups';
import { AddTopic } from './AddTopic';
import { AddUnit } from './AddUnit';
import { AddUsers } from './AddUsers';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    channelId: string;
    channelRoles: ChannelRolesSelectorResponse[];
    option: AddValueOption;
    onClose: (success: boolean) => void;
}

export type MembersSource = "user" | "group" | "unit" | "topic" | "channel" | "training" | "subordinates" | "allUsers" | "internalUsers" | "externalUsers";

////////////////////////////////////////////////////////////////////////////

const EditChannelAddMembers: React.FC<IProps> = (props) => {

    const { channelId, channelRoles, option: { id: sourceId, option: source, name }, onClose } = props;
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { addMembers, setError, loading: saving, error: memberOperationError, clearError } = useChannelMemberOperations();

    const [role, setRole] = useState<ChannelRoles>(ChannelRoles.Contributor);
    const [members, setMembers] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    let title = source == "user"
        ? t("Channel.Edit.Members.AddUsersTitle")
        : source == "group"
            ? t("Channel.Edit.Members.AddGroupsTitle")
            : source == "unit"
                ? name ? t("Channel.Edit.Members.AddUnitTypeTitle", { name: name }) : t("Channel.Edit.Members.AddUnitsTitle")
                : source == "topic"
                    ? t("Channel.Edit.Members.AddTopicsTitle")
                    : source == "channel"
                        ? t("Channel.Edit.Members.AddTopicsTitle")
                        : source == "training"
                            ? t("Channel.Edit.Members.AddTrainingTitle")
                            : source == "subordinates"
                                ? t("Channel.Edit.Members.AddSubordinatesTitle")
                                : source == "allUsers"
                                    ? t("Channel.Edit.Members.AddAllUsersTitle")
                                    : source == "internalUsers"
                                        ? t("Channel.Edit.Members.AddInternalUsersTitle")
                                        : source == "externalUsers"
                                            ? t("Channel.Edit.Members.AddExternalUsersTitle")
                                            : "";

    ////////////////////////////////////////////////////////////////////////////

    const handleAddMembers = async () => {
        const success = await addMembers({
            channelId,
            memberType: source == "user" ? MemberType.User : MemberType.Group,
            members,
            role
        });

        if (success) {
            onClose(true);
        }
    };

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (source == "allUsers" || source == "internalUsers" || source == "externalUsers") {
            setLoading(true);
            api.v1.group.getInternalGroups().then(response => {
                if (source == "allUsers") setMembers([response.data.find(p => p.groupType == GroupType.AllUsers)!.id]);
                if (source == "internalUsers") setMembers([response.data.find(p => p.groupType == GroupType.InternalUsers)!.id]);
                if (source == "externalUsers") setMembers([response.data.find(p => p.groupType == GroupType.ExternalUsers)!.id]);
            }).catch(e => MprcRequestSnackbarErrorReport(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return <>
        <MprcRequestIndicator
            visible={saving || loading} type={loading ? "request" : "post"}
        />

        <MprcRequestErrorDialog
            error={memberOperationError}
            onClose={clearError}
        />

        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={() => onClose(false)}
        >
            <MprcDialogTitleClose
                title={title}
                onClose={() => onClose(false)}
            />
            {source != "allUsers" && source != "internalUsers" && source != "externalUsers" &&
                <DialogContent sx={{ pt: 0 }}>
                    {source === "user" && <AddUsers channelId={channelId} onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "group" && <AddGroups channelId={channelId} onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "unit" && <AddUnit unitTypeId={sourceId} onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "topic" && <AddTopic onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "channel" && <AddChannel channelId={channelId} onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "training" && <AddTraining onError={setError} onItemsChange={members => setMembers(members)} />}
                    {source === "subordinates" && <AddSubordinates onError={setError} onItemsChange={members => setMembers(members)} />}
                </DialogContent>
            }
            <DialogActions>
                <Grid container spacing={2} m={1} ml={2} width="100%" alignItems="center">
                    <Grid size="grow">
                        <FormControl fullWidth>
                            <InputLabel>{t("Channel.Edit.Members.RoleLabel")}</InputLabel>
                            <Select
                                label={t("Channel.Edit.Members.RoleLabel")}
                                value={role}
                                onChange={e => setRole(e.target.value as ChannelRoles)}
                                defaultValue={ChannelRoles.Contributor}
                            >
                                {channelRoles.filter(x => x.id != ChannelRoles.Owner).map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid>
                        <Grid container justifyContent="center" alignItems="center" spacing={1} mt={1} width="100%">
                            <Grid>
                                <ButtonCancel onClick={() => onClose(false)} variant="contained" />
                            </Grid>
                            <Grid>
                                <Button
                                    variant="contained"
                                    disabled={members.length === 0 || saving}
                                    onClick={handleAddMembers}
                                >
                                    {t("Channel.Edit.Members.AddButtonText")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    </>;
};

export default EditChannelAddMembers;
